import { Component, Emit, Mixins, Prop, Watch } from 'vue-property-decorator'
import {
  CompanyTypeEnum,
  ProductCategory,
  Order,
  OrderEntry,
  PriceList,
  PriceListEntry,
  ProductCategoryCollection
} from '@/firestore'
import AuthMixin from '../../mixins/AuthMixin.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import { getHome } from '@/services/navigation.service'
import IconsMixin from '../../mixins/IconsMixin.vue'

interface ProductCategoryItem extends ProductCategory {
  children?: ProductCategoryItem[]
}

@Component({
  components: {LoadingPage}
})
export default class CategorySelectionModal extends Mixins(IconsMixin, AuthMixin) {
  @Prop({type: Object, default: null})
  public selectedOrderEntry!: OrderEntry

  @Prop({type: Object, default: null})
  public selectedPriceListEntry!: PriceListEntry | null

  @Prop({type: Object, default: null})
  public order!: Order

  @Prop({type: Object, default: null})
  public priceList!: PriceList

  public categories: ProductCategory[] = [] as ProductCategory[]
  public beverages: ProductCategory[] = [] as ProductCategory[]
  public alcoholicBeverages: ProductCategory[] = [] as ProductCategory[]
  public alcoholicBeveragesChildren: ProductCategory[] = [] as ProductCategory[]
  public foodItems: ProductCategory[] = [] as ProductCategory[]
  public water: ProductCategory[] = [] as ProductCategory[]
  public items: ProductCategoryItem[] = []
  public commonItems: ProductCategoryItem[] = []
  public flattenedItems: ProductCategoryItem[] = []
  public error: string = ''
  public tabs = null
  public show: boolean = false

  public commonActive: ProductCategoryItem[] = []
  public active: ProductCategoryItem[] = []

  public rules = {
    entry: [
      (v: ProductCategory) => (!!v) || 'Please select a category'
    ]
  }

  public async mounted() {
    if (this.userCompany.type !== CompanyTypeEnum.RESTAURANT) {
      return await this.$router.push({name: getHome(this.userCompany.type)})
    }

    await this.$firebase

    const productCategoryService = await this.$productCategoryService

    this.categories = await productCategoryService.listProductCategoriesByParentId('0')
    const alcoholicBeverages = await productCategoryService.findProductCategoryByNameAndLineage('Alcoholic Beverages', ['Food, Beverages & Tobacco', 'Beverages'])
    if (alcoholicBeverages) {
      this.alcoholicBeverages.push(alcoholicBeverages)
    }

    this.alcoholicBeveragesChildren = await productCategoryService.listProductCategoriesByParentId(this.alcoholicBeverages[0].id)

    const beverages = await productCategoryService.findProductCategoryByNameAndLineage('Beverages', ['Food, Beverages & Tobacco'])
    if (beverages) {
      this.beverages.push(beverages)
    }

    const foodItems = await productCategoryService.findProductCategoryByNameAndLineage('Food Items', ['Food, Beverages & Tobacco'])
    if (foodItems) {
      this.foodItems.push(foodItems)
    }

    const water = await productCategoryService.findProductCategoryByNameAndLineage('Water', ['Food, Beverages & Tobacco', 'Beverages'])
    if (water) {
      this.water.push(water)
    }

    this.commonItems.push(
      ...this.alcoholicBeveragesChildren.map(
        c => {
          return {...c, children: c.hasChildren ? [] : undefined}
        }
      ) as ProductCategoryItem[],
      ...this.water.map(
        c => {
          return {...c, children: c.hasChildren ? [] : undefined}
        }
      ) as ProductCategoryItem[]
    )

    this.items.push(
      ...this.beverages.map(
        c => {
          return {...c, children: c.hasChildren ? [] : undefined}
        }
      ) as ProductCategoryItem[],
      ...this.foodItems.map(
        c => {
          return {...c, children: c.hasChildren ? [] : undefined}
        }
      ) as ProductCategoryItem[]
    )

    const otherCategoryItems = this.categories.reduce(
      (acc, c) => {
        return (c.parentId === '0') ? [...acc, {
          ...c,
          children: c.hasChildren ? [] : undefined
        } as ProductCategoryItem] : acc
      }, [] as ProductCategoryItem[]
    )

    // populate the 'Other' drop down with all other top level categories
    this.items.push(
      {
        id: '0',
        name: 'Other',
        glCode: '',
        parentId: '0',
        lineage: [],
        children: otherCategoryItems
      } as unknown as ProductCategoryItem
    )

    this.flattenedItems.push(...this.items)
  }

  public async getChildren(item: any) {
    const productCategoryService = await this.$productCategoryService
    const newCategories = await productCategoryService.listProductCategoriesByParentId(item.id)

    const newCategoryItems = newCategories.reduce(
      (acc, category) => {
        if (!(category.parentId === item.id)) {
          return acc
        } else {
          return [...acc, {...category, children: category.hasChildren ? [] : undefined} as ProductCategoryItem]
        }
      }, [] as ProductCategoryItem[]
    )

    if (!newCategoryItems.length) {
      return item.children = undefined
    }

    item.children.push(...newCategoryItems)
    this.flattenedItems.push(...newCategoryItems)
  }

  public hide() {
    this.show = false
  }

  @Emit()
  public selectedEntry() {
  }

  @Watch('tabs')
  public tabsChange() {
    this.active = []
    this.commonActive = []
  }

  public async confirmSave() {
    if (!this.active[0] && !this.commonActive[0]) {
      return
    }

    const selectedCategory = new ProductCategory()
    const {children: _selectedChildren, ...selectedCategoryProps} = (this.commonActive[0] || this.active[0])
    Object.assign(selectedCategory, selectedCategoryProps)

    if (!selectedCategory) {
      return
    }

    if (this.selectedOrderEntry) {
      this.selectedOrderEntry.category = selectedCategory.fullName
    }

    if (this.selectedPriceListEntry) {
      this.selectedPriceListEntry.category = selectedCategory.fullName
    }

    this.hide()
  }

  public getCategory(entry: any) {
    if (!entry || !entry.category) {
      return 'N/A'
    }

    const lineageArray = entry.category.split(' > ')
    return lineageArray[lineageArray.length - 1].trim()
  }
}
